import type {
  PerksUsage,
  UserMembership,
  UserProfile,
} from '@/src/api/authApi';
import type { Membership, PlanFeature } from '@/src/api/membershipsApi';

/**
 * Membership fixtures for the preview routes — staging's real catalog, plus the
 * users needed to put it into each state. Shared across `app/components` so every
 * preview argues from the same three plans; the sibling of [[eventFixtures]].
 *
 * Nothing here is shipped UI. Dates are built RELATIVE to now so an expiry
 * always reads as a future date whenever the page is opened.
 */

const daysFromNow = (days: number) => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString();
};

export const planFeature = (
  name: string,
  limit: number | null
): PlanFeature => ({
  id: `pf-${name}`,
  limit,
  feature: { id: `f-${name}`, key: name, name, description: '' },
});

export const money = (amountInMinorUnits: number, display: string) => ({
  amountInMinorUnits,
  currency: 'INR',
  display,
});

export const membershipPlan = (
  tier: string,
  name: string,
  description: string,
  minorUnits: number,
  display: string,
  isRecurring: boolean,
  planFeatures: PlanFeature[] = []
): Membership => ({
  id: `plan-${tier}`,
  tier,
  name,
  description,
  kind: isRecurring ? 'access_pass' : 'trial',
  billingInterval: isRecurring ? 'month' : 'one_time',
  isRecurring,
  validityDays: isRecurring ? null : 30,
  // Untaxed and calendar-aligned by default: no tax profile, and today's charge
  // IS the full price — the shape of a purchase made on the 1st. A plan that
  // needs the two-box pricing overrides these (see ACCESS_PASS_PLAN). One-shot
  // plans get no prorated figures, as on staging.
  basePrice: money(minorUnits, display),
  taxAmount: money(0, '₹0'),
  tax: null,
  priceWithTax: money(minorUnits, display),
  proratedBasePrice: isRecurring ? money(minorUnits, display) : null,
  proratedTaxAmount: isRecurring ? money(0, '₹0') : null,
  proratedPriceWithTax: isRecurring ? money(minorUnits, display) : null,
  planFeatures,
});

// Staging's real figures, tax split and all, so the purchase screen's two-box
// pricing draws here exactly as it does in the app: ₹2,400 for a part-month
// today, ₹3,600 from the 1st.
export const ACCESS_PASS_PLAN: Membership = {
  ...membershipPlan(
    'access_pass',
    'Access Pass',
    'The Access Pass — your membership to Putting Scene, billed monthly.',
    360000,
    '₹3,600',
    true,
    [
      planFeature('PS originals', null),
      planFeature('Previews / Launches', null),
      planFeature('Guest passes', 2),
    ]
  ),
  basePrice: money(305085, '₹3,050.85'),
  taxAmount: money(54915, '₹549.15'),
  tax: { totalPercentage: '18.00' },
  proratedBasePrice: money(203390, '₹2,033.90'),
  proratedTaxAmount: money(36610, '₹366.10'),
  proratedPriceWithTax: money(240000, '₹2,400'),
};

// A restart in a month already paid for: the server prorates today down to zero.
export const PAID_UP_PLAN: Membership = {
  ...ACCESS_PASS_PLAN,
  proratedBasePrice: money(0, '₹0'),
  proratedTaxAmount: money(0, '₹0'),
  proratedPriceWithTax: money(0, '₹0'),
};

// Premium really does ship an empty perk list on staging — which is why the
// purchase screen's "What you get" box is conditional.
export const PREMIUM_PLAN = membershipPlan(
  'premium',
  'Access Pass Premium',
  'Monthly membership with unlimited access.',
  745000,
  '₹7,450',
  true
);

// The Trial Pass's zeroes: `0` is BLOCKED, not "none left".
export const TRIAL_PLAN = membershipPlan(
  'trial',
  'Trial Pass',
  'One-time trial pass. Cannot renew; one per user.',
  99900,
  '₹999',
  false,
  [
    planFeature('Tier-locked bookings', 2),
    planFeature('PS originals', 0),
    planFeature('Secret Menu', 0),
  ]
);

// An unseeded tier (so it draws the fallback icon) carrying copy long enough to
// hit the card's clamps.
export const LONG_COPY_PLAN = membershipPlan(
  'founders',
  "The Founders' Circle Access Pass — Bombay, Bengaluru & Goa",
  'Everything in the Access Pass, plus first refusal on every Originals table, a standing invitation to the members-only supper club, and two transferable guest passes a month for the people you keep meaning to take somewhere good.',
  1200000,
  '₹12,000',
  true
);

/**
 * The catalog in the order the API sends it (newest first) — NOT price order,
 * so anything reading it has to do its own sorting, same as in production.
 */
export const PREVIEW_CATALOG: Membership[] = [
  ACCESS_PASS_PLAN,
  PREMIUM_PLAN,
  TRIAL_PLAN,
];

// Nothing draws the perk block yet, so this is one valid shape rather than a
// modelled state — the Access Pass's, which uncapped all four categories.
// `resetsAt` is filled in per membership below: it's the same instant as
// `renewsAt`, and null on anything that lapses instead of renewing.
const UNLIMITED_PERKS: Omit<PerksUsage, 'resetsAt'> = {
  perks: {
    normal: { label: 'Events', bookings: 0, canBook: true, isUnlimited: true },
    ps_original: {
      label: 'PS Original',
      bookings: 0,
      canBook: true,
      isUnlimited: true,
    },
    preview: {
      label: 'Preview',
      bookings: 0,
      canBook: true,
      isUnlimited: true,
    },
    secret_menu: {
      label: 'Secret Menu',
      bookings: 0,
      canBook: true,
      isUnlimited: true,
    },
  },
};

/**
 * A membership the user holds. `lapsingOn` makes it one that ENDS — which is
 * the pair the wire actually sends: a renewing membership reports `renewsAt`
 * and nothing else, anything running out reports `expiresAt`.
 *
 * The nested `plan.id` matches [[membershipPlan]]'s, which is what joins a held
 * membership to a catalog row (see [[deriveMembershipUpsell]]) — so a fixture
 * user built from a `tier` in [[PREVIEW_CATALOG]] resolves to that plan.
 */
export const heldMembership = (
  tier: string,
  name: string,
  lapsingOn?: string
): UserMembership => {
  const renewsAt = lapsingOn ? null : daysFromNow(19);

  return {
    id: `um-${tier}`,
    plan: {
      id: `plan-${tier}`,
      name,
      tier,
      kind: tier === 'trial' ? 'trial' : 'access_pass',
    },
    status: lapsingOn ? 'cancelled' : 'active',
    source: 'payment',
    // Still the pass in effect either way: a cancelled membership keeps its
    // benefits to the end of the cycle it was paid for.
    phase: 'current',
    activatedAt: null,
    renewsAt,
    expiresAt: lapsingOn ?? null,
    daysRemaining: null,
    perksUsage: { ...UNLIMITED_PERKS, resetsAt: renewsAt },
  };
};

/**
 * A coupon/admin grant: `active`, but it ends — no subscription behind it, which
 * `source` is now the reliable tell for.
 */
export const couponMembership = (
  tier: string,
  name: string,
  days: number
): UserMembership => ({
  ...heldMembership(tier, name, daysFromNow(days)),
  status: 'active',
  source: 'coupon',
});

export const lapsingIn = daysFromNow;

export const previewUser = (
  membership: UserMembership | null,
  pastMemberships: UserMembership[] = []
): UserProfile => ({
  id: 'preview-user',
  isProfileComplete: true,
  mobile: '+919876543210',
  email: 'member@example.com',
  firstName: 'Abhimanyu',
  lastName: 'Sharma',
  fullName: 'Abhimanyu Sharma',
  socialMediaLink: null,
  membership,
  pendingMembership: null,
  hasUsedUpTrialPass: pastMemberships.some((m) => m.plan.tier === 'trial'),
  isEmailMissing: false,
  isOnlyEmailVerificationPending: false,
  areDetailsMissing: false,
});

/**
 * A signed-in preview: the `/me` user plus the `GET /me/memberships` history the
 * screens fetch beside it.
 */
export const previewSession = (
  membership: UserMembership | null,
  pastMemberships: UserMembership[] = []
) => ({ user: previewUser(membership, pastMemberships), pastMemberships });
